import { WaitlistItem } from '../pages/CollaboratePage/Types'
import { useTranslation } from 'react-i18next'
import { AccentBadge, AccentBadgeNumber } from '@/components/AccentBadge'
import { Button } from '@/UIComponents2/Button'
import CollaboratorsIcon from '@/CustomIcons/CollaboratorsIcon'
import DialogActionButtonsSection from './components/DialogActionButtonsSection'
import { useEffect, useState } from 'react'
import { Username } from '@/components/Username'
import { Checkbox } from '@/UIComponents2/Checkbox'
import { useRoomContext } from '@/RoomContext'
import { useMutate } from 'restful-react'
import { DialogBody } from '@/components/Dialog'

export default function RoomWaitlistDialog({
    roomWaitlist,
    onWaitlistedUserHandledResult,
}: {
    roomWaitlist: WaitlistItem[]
    onWaitlistedUserHandledResult: (profile: WaitlistItem, isAccepted: boolean) => void
}) {
    const { t } = useTranslation()
    const [roomState, roomDispatch] = useRoomContext()

    const [isAccepting, setIsAccepting] = useState(false)

    const allowUserAutoJoin = roomState.isSubroomsEnabled ? true : (roomState.allowUserAutoJoin ?? false)
    const isSubroom = !!roomState.parentRoom
    const disableAutoJoinCheckbox = isSubroom || roomState.isSubroomsEnabled

    const { mutate: updateRoomApi, loading } = useMutate({
        verb: 'PATCH',
        path: `/v1/rooms/${roomState.roomUrn || roomState.preRoomUrn || ''}`,
    })
    const onAllowUserAutoJoinToggle = () => {
        if (loading) {
            return
        }
        const allowUserAutoJoinNext = !allowUserAutoJoin

        roomDispatch({
            type: 'update_room_context',
            value: { allowUserAutoJoin: allowUserAutoJoinNext },
        })
        updateRoomApi({ allowUserAutoJoin: allowUserAutoJoinNext })
    }

    useEffect(() => {
        // check if all items are being joined in
        const allNotBeingJoinedIn = !roomWaitlist.every((item) => item.isBeingJoinedIn)
        if (allNotBeingJoinedIn) {
            setIsAccepting(false)
        }
    }, [roomWaitlist])

    const acceptAll = () => {
        setIsAccepting(true)
        roomWaitlist.forEach((item) => {
            onWaitlistedUserHandledResult(item, true)
        })
    }

    const denyAll = () => {
        roomWaitlist.forEach((item) => {
            onWaitlistedUserHandledResult(item, false)
        })
    }

    return (
        <div className="flex flex-col w-full">
            <DialogBody className="flex flex-col gap-6">
                <AccentBadge subText={t('Users in the waiting room', { count: roomWaitlist.length })}>
                    <AccentBadgeNumber>
                        <span>{String(roomWaitlist.length)}</span>
                        <CollaboratorsIcon className="absolute top-[10px] right-[-18px]" style={{ fontSize: '16px' }} />
                    </AccentBadgeNumber>
                </AccentBadge>

                <div className="flex flex-col gap-2 ">
                    {roomWaitlist.map((item, i) => {
                        return (
                            <WaitlistItemContent
                                key={i}
                                item={item}
                                isBeingJoinedIn={item.isBeingJoinedIn}
                                onWaitlistedUserHandledResult={onWaitlistedUserHandledResult}
                            />
                        )
                    })}
                </div>

                <div className="flex justify-start">
                    <Checkbox
                        checked={!allowUserAutoJoin}
                        label={t(`Enable waiting room`) || ''}
                        onChange={onAllowUserAutoJoinToggle}
                        disabled={disableAutoJoinCheckbox}
                    />
                </div>
            </DialogBody>

            <DialogActionButtonsSection
                leftButtonText={t('Deny all')}
                rightButtonText={t('Accept all')}
                onLeftButtonClicked={denyAll}
                onRightButtonClicked={acceptAll}
                isRightButtonDisabled={isAccepting}
            />
        </div>
    )
}

function WaitlistItemContent({
    item,
    isBeingJoinedIn,
    onWaitlistedUserHandledResult,
}: {
    item: WaitlistItem
    isBeingJoinedIn: boolean
    onWaitlistedUserHandledResult: (profile: WaitlistItem, isAccepted: boolean) => void
}) {
    const { t } = useTranslation()
    const onAllow = () => !item.isBeingJoinedIn && onWaitlistedUserHandledResult(item, true)
    const onDeny = () => !item.isBeingJoinedIn && onWaitlistedUserHandledResult(item, false)

    return (
        <WaitlistItemContainer>
            <div className="flex-1 pl-5">
                <Username
                    isGuest={item.isGuest}
                    username={item.profile.username}
                    displayName={item.profile.displayName ?? undefined}
                    suffix
                />
            </div>
            <div className="flex gap-1">
                <Button variant="secondary" onClick={onDeny} className="py-[7px]" disabled={isBeingJoinedIn}>
                    {t('Deny')}
                </Button>
                <Button variant="primary" onClick={onAllow} className="py-[7px]" disabled={isBeingJoinedIn}>
                    {t('Allow')}
                </Button>
            </div>
        </WaitlistItemContainer>
    )
}

function WaitlistItemContainer({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex p-[1px] justify-between items-center gap-2 shadow-glow transparent border border-white/10 border-t-white/20 rounded-full">
            {children}
        </div>
    )
}
